import type { Block, SpaceEntry } from './types.js'
import { AUTOSAVE_DEBOUNCE_MS, FETCH_TIMEOUT_MS, LILODATA_BLOCK_ID, LOCAL_CONSOLIDATION_DEBOUNCE_MS } from './constants.js'
import {
  createLilodataBlock,
  extractLilodataBlock,
  isLilodataBlock,
  updateLilodataText
} from './lilodata.js'
import { getDefaultPaperType } from './paper.js'
import { deriveSpaceIded } from './lib/spaceIdentity.js'
import { instantiateSeedSpace, serializeSeedComparableSpace } from './lib/seedSpace.js'
import { generateId } from './id.js'
import {
  DatabaseMemory,
  deriveLocalSpaceAvailability,
  type AccountVolumeMountMode,
  type AccountVolumeMountResult,
  type AccountUnmountAuthority,
  type AccountUnmountMode,
  type AccountUnmountResult,
  type LocalAccountProfile,
  type LocalAccountPassport,
  type LocalMemoryIndexRow,
  type LocalShelfSpace,
  type LocalSpaceAvailability,
  type LocalSpaceMeta,
  type SyncAcknowledgement,
  type LocalVolumeAuthority,
  type LocalVolumeBootDamage,
  type LocalVolumeBootSnapshot,
  type LocalWorkbenchMarker,
  type PayloadState,
  type RemoteSpaceMetaInput,
  type ReplaceLocalBlobFromRemoteInput,
  type ReplaceLocalBlobFromRemoteResult,
  type SeedSpaceProof,
  type SpaceBlobRow,
  type SpacePayloadProof,
  type UnsafeOwnerShelfRow,
  type VolumeShelfExportPreflight,
  type WorkbenchRecoveryEvidence
} from './lib/databaseMemory.js'
import { selectLandingSpace } from './lib/indexNavigation.js'
import { cloneSpaceEntry, cloneBlock, isValidBlock, decodeSpacePayload, encodeSpacePayload } from './lib/spaceCodec.js'
import { sortBlocks } from './lib/editorMemory.js'

export type LocalConsolidationActivity = {
  active: boolean
  durationMs?: number
}

export type LocalWriteRefusal = 'space_missing' | 'space_deleted'

export type ActiveDeskState = 'provisional' | 'filing' | 'filed'

export class LocalBootRestartRequired extends Error {
  constructor() {
    super('Local volume boot blocked: mounted volume changed')
    this.name = 'LocalBootRestartRequired'
  }
}

export type LocalVolumeBootBlockedReason =
  | LocalVolumeBootDamage
  | 'mixed_owner_shelf'
  | 'workbench_authority'
  | 'editor_identity'
  | 'loose_editor'
  | 'recovery_failed'

export class LocalVolumeBootBlocked extends Error {
  constructor(readonly reason: LocalVolumeBootBlockedReason) {
    const detail = reason === 'mixed_owners' || reason === 'mixed_owner_shelf'
      ? 'mixed owner shelf'
      : reason === 'invalid_workbench' || reason === 'workbench_authority'
        ? 'workbench authority mismatch'
        : reason === 'editor_identity'
          ? 'editor identity contradicts workbench'
          : reason === 'loose_editor'
            ? 'loose editor manuscript has no safe provenance'
            : reason
    super(`Local volume boot blocked: ${detail}`)
    this.name = 'LocalVolumeBootBlocked'
  }
}

export type LocalBootLanding =
  | { kind: 'opened' | 'provisional' | 'proven-empty' }
  | {
      kind: 'memory'
      reason: 'unavailable' | 'damaged-row'
      intendedSpaceId: string
    }

export type AccountMemoryMountResult =
  | {
      ok: true
      status: Extract<AccountVolumeMountResult, { ok: true }>['status']
      passport: LocalAccountPassport
      blocks: SpaceEntry[]
    }
  | Extract<AccountVolumeMountResult, { ok: false }>

type PendingVolumeBlocks = {
  metaBlock?: Block
  authority: LocalVolumeAuthority
  blocks: SpaceEntry[]
}

type PendingVolumeDocument = {
  authority: LocalVolumeAuthority
  blocksWithMeta: SpaceEntry[]
}

type VolumeWorkbenchRestoreResult =
  | { kind: 'opened', blocks: SpaceEntry[] }
  | { kind: 'proven-empty' }
  | {
      kind: 'memory'
      reason: 'unavailable' | 'damaged-row'
      intendedSpaceId: string
    }

type RecoveryEditorEvidence =
  | { kind: 'empty' }
  | { kind: 'disposable', proof: SpacePayloadProof }
  | { kind: 'meaningful' }

type BootRowPayloadResult =
  | { kind: 'ready', blocksWithMeta: SpaceEntry[] }
  | Extract<VolumeWorkbenchRestoreResult, { kind: 'memory' }>

export class Storage {
  private database = new DatabaseMemory()
  private saveTimer?: number
  private consolidationTimer?: number
  private pendingWorkbench?: PendingVolumeBlocks
  private pendingShelf?: PendingVolumeBlocks
  private pendingConsolidationAuthority?: LocalVolumeAuthority
  private activeDeskState: ActiveDeskState = 'filed'
  private provisionalComparable?: string
  private bootLanding: LocalBootLanding = { kind: 'proven-empty' }
  private pendingFilingDocument?: PendingVolumeDocument
  private filingPromise?: Promise<LocalSpaceMeta>
  private localWriteTail: Promise<unknown> = Promise.resolve()
  private localWriteEpoch = 0
  private seedBlocksCache?: Block[]
  private volumeAuthority?: LocalVolumeAuthority
  private mountedPassport?: LocalAccountPassport
  private metaBlock?: Block
  private metaNeedsSave = false
  private readonly autosaveDebounceMs: number
  private readonly consolidationDebounceMs: number
  private onConsolidationActivity?: (activity: LocalConsolidationActivity) => void
  private onConsolidated?: (authority: LocalVolumeAuthority) => void
  private onWriteRefused?: (reason: LocalWriteRefusal) => void

  constructor(opts: {
    autosaveDebounceMs?: number
    consolidationDebounceMs?: number
    onConsolidationActivity?: (activity: LocalConsolidationActivity) => void
    onConsolidated?: (authority: LocalVolumeAuthority) => void
  } = {}) {
    this.autosaveDebounceMs = opts.autosaveDebounceMs ?? AUTOSAVE_DEBOUNCE_MS
    this.consolidationDebounceMs = opts.consolidationDebounceMs ?? LOCAL_CONSOLIDATION_DEBOUNCE_MS
    this.onConsolidationActivity = opts.onConsolidationActivity
    this.onConsolidated = opts.onConsolidated
  }

  setConsolidationActivityListener(listener?: (activity: LocalConsolidationActivity) => void): void {
    this.onConsolidationActivity = listener
  }

  setConsolidatedListener(
    listener?: (authority: LocalVolumeAuthority) => void
  ): void {
    this.onConsolidated = listener
  }

  setWriteRefusedListener(
    listener?: (reason: LocalWriteRefusal) => void
  ): void {
    this.onWriteRefused = listener
  }

  async initialize(opts?: { skipSeed?: boolean }): Promise<SpaceEntry[]> {
    return this.initializeAttempt(opts, 0)
  }

  private async initializeAttempt(
    opts: { skipSeed?: boolean } | undefined,
    attempt: number
  ): Promise<SpaceEntry[]> {
    try {
      return await this.initializeFromCurrentVolume(opts)
    } catch (error) {
      if (error instanceof LocalBootRestartRequired && attempt === 0) {
        return this.initializeAttempt(opts, 1)
      }
      throw error
    }
  }

  private async initializeFromCurrentVolume(opts?: { skipSeed?: boolean }): Promise<SpaceEntry[]> {
    await this.database.open()
    const boot = await this.database.resolveVolumeBoot(generateId())
    if (!boot.ok) {
      throw new LocalVolumeBootBlocked(boot.reason)
    }
    const snapshot = boot.snapshot
    this.mountedPassport = snapshot.passport
      ? this.clonePassport(snapshot.passport)
      : undefined
    this.volumeAuthority = snapshot.passport
      ? {
          owner_id: snapshot.passport.owner_id,
          mount_id: snapshot.passport.mount_id
        }
      : null
    let restored = await this.restoreVolumeWorkbench(snapshot)
    this.activeDeskState = 'filed'
    this.provisionalComparable = undefined
    const provisional = !opts?.skipSeed && (
      restored.kind === 'proven-empty' || restored.kind === 'memory'
    )
      ? await this.prepareProvisionalWelcome()
      : undefined
    this.bootLanding = this.resolveBootLanding(restored, Boolean(provisional))
    const blocks = provisional ?? this.restoreResultBlocks(restored)
    return blocks.map(block => cloneSpaceEntry(block))
  }

  async mountAccountMemory(input: {
    ownerId: string
    profile: LocalAccountProfile
    mode: AccountVolumeMountMode
    catalogue: RemoteSpaceMetaInput[]
  }): Promise<AccountMemoryMountResult> {
    await this.waitForActiveFiling()
    this.cancelPendingSave()
    const previousPassport = this.mountedPassport
      ? this.clonePassport(this.mountedPassport)
      : undefined
    const previousAuthority = this.volumeAuthority === undefined
      ? undefined
      : this.cloneVolumeAuthority(this.volumeAuthority)
    const previousMetaBlock = this.metaBlock
      ? cloneBlock(this.metaBlock)
      : undefined
    const previousMetaNeedsSave = this.metaNeedsSave
    const previousDeskState = this.activeDeskState
    const previousProvisionalComparable = this.provisionalComparable
    const previousBootLanding = this.getBootLanding()
    const result = await this.database.mountAccountVolume({
      passport: {
        kind: 'verified',
        owner_id: input.ownerId,
        mount_id: generateId(),
        profile: { ...input.profile }
      },
      mode: input.mode,
      catalogue: input.catalogue
    })
    if (!result.ok) return result

    this.mountedPassport = this.clonePassport(result.passport)
    this.volumeAuthority = {
      owner_id: result.passport.owner_id,
      mount_id: result.passport.mount_id
    }
    try {
      const snapshot = await this.database.loadVolumeBootSnapshot()
      this.assertSnapshotAuthority(snapshot)
      const restored = await this.restoreVolumeWorkbench(snapshot)
      this.activeDeskState = 'filed'
      this.provisionalComparable = undefined
      const provisional = restored.kind === 'proven-empty' || restored.kind === 'memory'
        ? await this.prepareProvisionalWelcome()
        : undefined
      this.bootLanding = this.resolveBootLanding(restored, Boolean(provisional))
      const blocks = provisional ?? this.restoreResultBlocks(restored)
      return {
        ok: true,
        status: result.status,
        passport: this.clonePassport(result.passport),
        blocks: blocks.map(block => cloneSpaceEntry(block))
      }
    } catch (error) {
      this.mountedPassport = previousPassport
      this.volumeAuthority = previousAuthority
      this.metaBlock = previousMetaBlock
      this.metaNeedsSave = previousMetaNeedsSave
      this.activeDeskState = previousDeskState
      this.provisionalComparable = previousProvisionalComparable
      this.bootLanding = previousBootLanding
      throw error
    }
  }

  async verifyMountedAccountPassport(
    ownerId: string,
    profile: LocalAccountProfile
  ): Promise<boolean> {
    const passport = this.mountedPassport
    if (!passport || passport.owner_id !== ownerId) return false
    const authority = {
      owner_id: passport.owner_id,
      mount_id: passport.mount_id
    }
    const next: LocalAccountPassport = {
      kind: 'verified',
      ...authority,
      profile: { ...profile }
    }
    const verified = await this.database.compareAndSetPassport(authority, next)
    if (!verified) return false
    this.mountedPassport = this.clonePassport(next)
    this.volumeAuthority = { ...authority }
    return true
  }

  async unmountAccountMemory(
    authority: AccountUnmountAuthority,
    mode: AccountUnmountMode = 'guarded',
    safeSeedProofs: SeedSpaceProof[] = []
  ): Promise<AccountUnmountResult> {
    await this.waitForActiveFiling()
    this.cancelPendingSave()
    const result = await this.database.unmountAccountVolume(authority, mode, safeSeedProofs)
    if (result.ok) {
      this.metaBlock = undefined
      this.mountedPassport = undefined
      this.volumeAuthority = null
      this.activeDeskState = 'filed'
      this.provisionalComparable = undefined
      this.bootLanding = { kind: 'proven-empty' }
    }
    return result
  }

  private async prepareProvisionalWelcome(): Promise<SpaceEntry[] | undefined> {
    const seedBlocks = await this.loadSeedBlocks()
    const fallbackPaperLabel = getDefaultPaperType().label
    const seeded = seedBlocks
      ? instantiateSeedSpace(seedBlocks, { fallbackPaperLabel })
      : { blocks: [] as Block[], metaBlock: undefined }
    const metaBlock = seeded.metaBlock ?? createLilodataBlock(fallbackPaperLabel)
    this.metaBlock = cloneBlock(metaBlock)
    this.activeDeskState = 'provisional'
    this.provisionalComparable = serializeSeedComparableSpace(
      seeded.blocks,
      metaBlock
    )
    return seeded.blocks.map(block => cloneSpaceEntry(block))
  }

  async loadBlocks(): Promise<SpaceEntry[]> {
    const snapshot = await this.database.loadVolumeBootSnapshot()
    this.assertSnapshotAuthority(snapshot)
    return this.restoreResultBlocks(
      await this.restoreVolumeWorkbench(snapshot)
    )
  }

  async loadLocalRescueBlobs(): Promise<SpaceBlobRow[]> {
    return this.database.loadUnscopedRescueBlobs(generateId())
  }

  async loadSpaceFromShelf(
    spaceId: string,
    authority = this.captureVolumeAuthority(),
    opts?: { consumeSeed?: SeedSpaceProof }
  ): Promise<SpaceEntry[] | undefined> {
    await this.waitForActiveFiling()
    const shelf = await this.database.loadVolumeShelfSpace(authority, spaceId)
    if (!shelf || shelf.meta.deleted_at || !shelf.blob) return undefined
    let blocksWithMeta: SpaceEntry[]
    try {
      blocksWithMeta = await decodeSpacePayload(shelf.blob.payload)
      if (!this.hasSpaceIdentity(blocksWithMeta, spaceId)) {
        throw new Error('payload identity mismatch')
      }
    } catch {
      await this.setVolumePayloadState(authority, spaceId, 'known', {
        errorAt: new Date().toISOString()
      })
      throw new Error('Shelf open blocked: invalid payload')
    }
    const replaced = await this.database.replaceEditorBlocksWithWorkbenchMarker(
      blocksWithMeta,
      this.buildWorkbenchMarker(authority, spaceId),
      opts?.consumeSeed
    )
    if (!replaced.ok) {
      throw new Error(`Shelf open blocked: ${replaced.reason}`)
    }
    this.activeDeskState = 'filed'
    this.provisionalComparable = undefined
    this.bootLanding = { kind: 'opened' }
    return this.adoptLoadedBlocks(blocksWithMeta)
  }

  private async restoreVolumeWorkbench(
    snapshot: LocalVolumeBootSnapshot,
    recoveryAttempt = 0
  ): Promise<VolumeWorkbenchRestoreResult> {
    this.assertSnapshotAuthority(snapshot)
    const authority = this.captureVolumeAuthority()
    const ownerId = authority?.owner_id ?? null
    if (snapshot.spaces_meta.some(row => row.owner_id !== ownerId)) {
      throw new LocalVolumeBootBlocked('mixed_owner_shelf')
    }

    const workbench = snapshot.workbench
    if (workbench) {
      if (!this.matchesWorkbenchAuthority(workbench, authority)) {
        throw new LocalVolumeBootBlocked('workbench_authority')
      }
      const activeMeta = snapshot.spaces_meta.find(row => row.space_id === workbench.space_id)
      if (activeMeta && !activeMeta.deleted_at) {
        if (snapshot.editor_blocks.length) {
          if (!this.hasSpaceIdentity(snapshot.editor_blocks, workbench.space_id)) {
            throw new LocalVolumeBootBlocked('editor_identity')
          }
          return {
            kind: 'opened',
            blocks: this.adoptLoadedBlocks(snapshot.editor_blocks)
          }
        }
        const activePayload = await this.resolveBootRowPayload(
          activeMeta,
          snapshot.active_blob,
          authority
        )
        if (activePayload.kind === 'memory') return activePayload
        return this.installRecoveredWorkbench({
          snapshot,
          authority,
          targetMeta: activeMeta,
          targetPayload: snapshot.active_blob!.payload,
          blocksWithMeta: activePayload.blocksWithMeta,
          selection: 'marker',
          editorEvidence: { kind: 'empty' },
          recoveryAttempt
        })
      }
    }

    const editorEvidence = await this.classifyRecoveryEditor(snapshot, authority)
    if (editorEvidence.kind === 'meaningful') {
      throw new LocalVolumeBootBlocked('loose_editor')
    }
    const landing = selectLandingSpace(snapshot.spaces_meta)
    if (!landing) {
      this.metaBlock = undefined
      return { kind: 'proven-empty' }
    }
    const blob = await this.database.loadSpaceBlob(landing.space_id)
    const landingPayload = await this.resolveBootRowPayload(
      landing,
      blob,
      authority
    )
    if (landingPayload.kind === 'memory') return landingPayload
    return this.installRecoveredWorkbench({
      snapshot,
      authority,
      targetMeta: landing,
      targetPayload: blob!.payload,
      blocksWithMeta: landingPayload.blocksWithMeta,
      selection: 'landing',
      editorEvidence,
      recoveryAttempt
    })
  }

  private async resolveBootRowPayload(
    meta: LocalSpaceMeta,
    blob: { payload: string } | undefined,
    authority: LocalVolumeAuthority
  ): Promise<BootRowPayloadResult> {
    const availability = deriveLocalSpaceAvailability(meta, Boolean(blob))
    if (availability !== 'ready') {
      return this.buildMemoryLanding(meta.space_id, availability)
    }
    let blocksWithMeta: SpaceEntry[]
    try {
      blocksWithMeta = await decodeSpacePayload(blob!.payload)
      if (!this.hasSpaceIdentity(blocksWithMeta, meta.space_id)) {
        throw new Error('payload identity mismatch')
      }
    } catch {
      await this.setVolumePayloadState(authority, meta.space_id, 'known', {
        errorAt: new Date().toISOString()
      })
      return this.buildMemoryLanding(meta.space_id, 'damaged')
    }
    return { kind: 'ready', blocksWithMeta }
  }

  private buildMemoryLanding(
    intendedSpaceId: string,
    availability: Exclude<LocalSpaceAvailability, 'ready'>
  ): Extract<VolumeWorkbenchRestoreResult, { kind: 'memory' }> {
    this.metaBlock = undefined
    return {
      kind: 'memory',
      reason: availability === 'damaged' ? 'damaged-row' : 'unavailable',
      intendedSpaceId
    }
  }

  private resolveBootLanding(
    restored: VolumeWorkbenchRestoreResult,
    hasProvisionalSheet: boolean
  ): LocalBootLanding {
    if (restored.kind === 'memory') return { ...restored }
    if (hasProvisionalSheet) {
      return restored.kind === 'proven-empty' && this.isDeactivatedAccountVolume()
        ? { kind: 'proven-empty' }
        : { kind: 'provisional' }
    }
    return { kind: restored.kind }
  }

  private async classifyRecoveryEditor(
    snapshot: LocalVolumeBootSnapshot,
    authority: LocalVolumeAuthority
  ): Promise<RecoveryEditorEvidence> {
    if (!snapshot.editor_blocks.length) return { kind: 'empty' }
    const { metaBlock } = extractLilodataBlock(snapshot.editor_blocks)
    if (!metaBlock) return { kind: 'meaningful' }
    const ownerId = authority?.owner_id ?? null
    const snapshotMeta = snapshot.spaces_meta.find(row => row.space_id === metaBlock.ided[0])
    if (
      !snapshotMeta
      || snapshotMeta.deleted_at
      || snapshotMeta.owner_id !== ownerId
      || snapshotMeta.space_ed !== metaBlock.ided[1]
    ) {
      return { kind: 'meaningful' }
    }
    const shelf = await this.database.loadVolumeShelfSpace(authority, snapshotMeta.space_id)
    if (
      !shelf
      || !shelf.blob
      || shelf.meta.deleted_at
      || shelf.meta.owner_id !== ownerId
      || shelf.meta.space_ed !== snapshotMeta.space_ed
      || shelf.blob.space_id !== snapshotMeta.space_id
    ) {
      return { kind: 'meaningful' }
    }
    try {
      const packedBlocks = await decodeSpacePayload(shelf.blob.payload)
      if (
        !this.hasSpaceIdentity(packedBlocks, snapshotMeta.space_id)
        || !blocksMatchStructurally(snapshot.editor_blocks, packedBlocks)
      ) {
        return { kind: 'meaningful' }
      }
    } catch {
      return { kind: 'meaningful' }
    }
    return {
      kind: 'disposable',
      proof: {
        space_id: snapshotMeta.space_id,
        space_ed: snapshotMeta.space_ed,
        payload: shelf.blob.payload
      }
    }
  }

  private async installRecoveredWorkbench(input: {
    snapshot: LocalVolumeBootSnapshot
    authority: LocalVolumeAuthority
    targetMeta: LocalSpaceMeta
    targetPayload: string
    blocksWithMeta: SpaceEntry[]
    selection: WorkbenchRecoveryEvidence['selection']
    editorEvidence: Exclude<RecoveryEditorEvidence, { kind: 'meaningful' }>
    recoveryAttempt: number
  }): Promise<VolumeWorkbenchRestoreResult> {
    const recovery: WorkbenchRecoveryEvidence = {
      selection: input.selection,
      expected_workbench: input.snapshot.workbench
        ? { ...input.snapshot.workbench }
        : undefined,
      expected_editor_blocks: input.snapshot.editor_blocks.map(block => cloneSpaceEntry(block)),
      target: {
        space_id: input.targetMeta.space_id,
        space_ed: input.targetMeta.space_ed,
        payload: input.targetPayload
      },
      disposable_editor: input.editorEvidence.kind === 'disposable'
        ? { ...input.editorEvidence.proof }
        : undefined
    }
    const replaced = await this.database.replaceEditorBlocksWithWorkbenchMarker(
      input.blocksWithMeta,
      this.buildWorkbenchMarker(input.authority, input.targetMeta.space_id),
      undefined,
      recovery
    )
    if (!replaced.ok) {
      if (
        input.recoveryAttempt === 0
        && (
          replaced.reason === 'integrity_mismatch'
          || replaced.reason === 'space_missing'
          || replaced.reason === 'space_deleted'
        )
      ) {
        const refreshed = await this.database.loadVolumeBootSnapshot()
        this.assertSnapshotAuthority(refreshed)
        return this.restoreVolumeWorkbench(refreshed, 1)
      }
      throw new LocalVolumeBootBlocked('recovery_failed')
    }
    return {
      kind: 'opened',
      blocks: this.adoptLoadedBlocks(input.blocksWithMeta)
    }
  }

  private restoreResultBlocks(result: VolumeWorkbenchRestoreResult): SpaceEntry[] {
    return result.kind === 'opened' ? result.blocks : []
  }

  private adoptLoadedBlocks(blocksWithMeta: SpaceEntry[]): SpaceEntry[] {
    const sorted = blocksWithMeta.slice().sort((a, b) => {
      const aId = a?.ided?.[0] ?? ''
      const bId = b?.ided?.[0] ?? ''
      return aId.localeCompare(bId)
    })
    const clones = sorted.map(block => cloneSpaceEntry(block))
    const { blocks, metaBlock } = extractLilodataBlock(clones)
    const normalizedMeta = this.normalizeMetaBlock(blocks, metaBlock)
    this.metaBlock = normalizedMeta ? cloneBlock(normalizedMeta) : undefined
    return blocks
  }

  getActiveDeskState(): ActiveDeskState {
    return this.activeDeskState
  }

  hasProvisionalAuthorship(blocks: SpaceEntry[]): boolean {
    if (
      this.activeDeskState !== 'provisional'
      || !this.metaBlock
      || this.provisionalComparable === undefined
    ) return false
    return serializeSeedComparableSpace(blocks, this.metaBlock)
      !== this.provisionalComparable
  }

  getBootLanding(): LocalBootLanding {
    return { ...this.bootLanding }
  }

  async waitForActiveFiling(): Promise<void> {
    await this.filingPromise
  }

  async setProvisionalWelcome(
    blocks: SpaceEntry[],
    metaBlock: Block,
    authority = this.captureVolumeAuthority()
  ): Promise<void> {
    await this.waitForActiveFiling()
    this.cancelPendingSave()
    if (!await this.isVolumeAuthorityCurrent(authority)) {
      throw new Error('Provisional Welcome blocked: mounted volume changed')
    }
    this.metaBlock = cloneBlock(metaBlock)
    this.activeDeskState = 'provisional'
    this.provisionalComparable = serializeSeedComparableSpace(blocks, metaBlock)
    this.bootLanding = { kind: 'provisional' }
    this.pendingFilingDocument = undefined
  }

  async fileProvisionalSpace(
    blocks: SpaceEntry[],
    authority = this.captureVolumeAuthority()
  ): Promise<LocalSpaceMeta> {
    if (this.isDeactivatedAccountVolume()) {
      throw new Error('Space filing blocked: Account memory is read-only')
    }
    if (this.activeDeskState === 'filed') {
      const existing = await this.getActiveSpaceMeta(authority)
      if (!existing || existing.deleted_at) {
        throw new Error('Space filing blocked: active space is not live')
      }
      return existing
    }
    const metaBlock = this.metaBlock
    if (!metaBlock) {
      throw new Error('Space filing blocked: missing /lilodata')
    }
    this.pendingFilingDocument = {
      authority: this.cloneVolumeAuthority(authority),
      blocksWithMeta: [
        ...blocks.map(block => cloneSpaceEntry(block)),
        cloneBlock(metaBlock)
      ]
    }
    if (!this.filingPromise) {
      this.activeDeskState = 'filing'
      const filing = this.drainProvisionalFiling()
      this.filingPromise = filing
      void filing.finally(() => {
        if (this.filingPromise === filing) {
          this.filingPromise = undefined
        }
      }).catch(() => undefined)
    }
    return this.filingPromise
  }

  private async drainProvisionalFiling(): Promise<LocalSpaceMeta> {
    const first = this.pendingFilingDocument
    this.pendingFilingDocument = undefined
    if (!first) {
      this.activeDeskState = 'provisional'
      throw new Error('Space filing blocked: missing manuscript')
    }
    if (this.isDeactivatedAccountVolume()) {
      this.activeDeskState = 'provisional'
      throw new Error('Space filing blocked: Account memory is read-only')
    }
    let filed: Awaited<ReturnType<DatabaseMemory['fileVolumeSpace']>>
    try {
      filed = await this.database.fileVolumeSpace({
        authority: first.authority,
        blocksWithMeta: first.blocksWithMeta,
        workbench: true,
        dirty: Boolean(first.authority),
        payloadState: 'hydrated'
      })
    } catch (error) {
      this.activeDeskState = 'provisional'
      throw error
    }
    if (!filed.ok) {
      this.activeDeskState = 'provisional'
      throw new Error(`Space filing blocked: ${filed.reason}`)
    }
    this.provisionalComparable = undefined

    let latestMeta = filed.meta
    try {
      while (true) {
        const pending = this.takePendingFilingDocument()
        if (!pending) break
        const updated = await this.database.updateVolumeSpace({
          authority: pending.authority,
          blocksWithMeta: pending.blocksWithMeta,
          workbench: true,
          dirty: Boolean(pending.authority),
          payloadState: 'hydrated'
        })
        if (!updated.ok) {
          throw new Error(`Space update blocked: ${updated.reason}`)
        }
        latestMeta = updated.meta
      }
    } catch (error) {
      this.activeDeskState = 'filed'
      throw error
    }
    this.activeDeskState = 'filed'
    this.bootLanding = { kind: 'opened' }
    this.onConsolidated?.(first.authority)
    this.onConsolidationActivity?.({ active: false })
    return latestMeta
  }

  private takePendingFilingDocument(): PendingVolumeDocument | undefined {
    const pending = this.pendingFilingDocument
    this.pendingFilingDocument = undefined
    return pending
  }

  private queueFilingSnapshot(
    blocks: SpaceEntry[],
    authority: LocalVolumeAuthority
  ): void {
    if (this.activeDeskState !== 'filing' || !this.metaBlock) return
    this.pendingFilingDocument = {
      authority: this.cloneVolumeAuthority(authority),
      blocksWithMeta: [
        ...blocks.map(block => cloneSpaceEntry(block)),
        cloneBlock(this.metaBlock)
      ]
    }
  }

  saveDocument(blocks: SpaceEntry[]): void {
    this.saveWorkbench(blocks)
  }

  saveWorkbench(
    blocks: SpaceEntry[],
    authority = this.captureVolumeAuthority()
  ): void {
    if (this.activeDeskState === 'provisional') return
    if (this.activeDeskState === 'filing') {
      this.queueFilingSnapshot(blocks, authority)
      return
    }
    this.scheduleWorkbenchSave(blocks, authority)
  }

  private scheduleWorkbenchSave(blocks: SpaceEntry[], authority: LocalVolumeAuthority): void {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer)
    }

    this.pendingWorkbench = {
      metaBlock: this.getMetaBlock(),
      authority: this.cloneVolumeAuthority(authority),
      blocks: blocks.map(block => cloneSpaceEntry(block))
    }

    this.saveTimer = window.setTimeout(() => {
      void this.writePendingSurvivalDocument().catch(() => undefined)
    }, this.autosaveDebounceMs)
  }

  commitDocument(blocks: SpaceEntry[]): void {
    this.commitShelfSnapshot(blocks)
  }

  commitShelfSnapshot(
    blocks: SpaceEntry[],
    authority = this.captureVolumeAuthority()
  ): void {
    if (this.activeDeskState === 'provisional') return
    if (this.activeDeskState === 'filing') {
      this.queueFilingSnapshot(blocks, authority)
      return
    }
    this.scheduleWorkbenchSave(blocks, authority)
    this.pendingShelf = {
      metaBlock: this.getMetaBlock(),
      authority: this.cloneVolumeAuthority(authority),
      blocks: blocks.map(block => cloneSpaceEntry(block))
    }
    this.scheduleShelfConsolidation(authority)
  }

  scheduleShelfConsolidation(authority = this.captureVolumeAuthority()): void {
    if (this.consolidationTimer) {
      clearTimeout(this.consolidationTimer)
    }
    this.pendingConsolidationAuthority = this.cloneVolumeAuthority(authority)
    this.onConsolidationActivity?.({
      active: true,
      durationMs: this.consolidationDebounceMs
    })
    this.consolidationTimer = window.setTimeout(() => {
      void this.finishPendingConsolidation().catch(() => undefined)
    }, this.consolidationDebounceMs)
  }

  private async doSaveDocumentForAuthority(
    blocks: SpaceEntry[],
    authority: LocalVolumeAuthority,
    metaBlock = this.metaBlock
  ): Promise<void> {
    if (this.activeDeskState === 'provisional') return
    if (this.activeDeskState === 'filing') {
      this.queueFilingSnapshot(blocks, authority)
      await this.waitForActiveFiling()
      return
    }
    const payload = this.buildActivePayload(blocks, metaBlock)
    if (!payload.length) {
      const cleared = await this.database.clearLocalMemoryForVolume(authority)
      if (!cleared) {
        throw new Error('Local clear blocked: mounted volume changed')
      }
      this.onConsolidationActivity?.({ active: false })
      return
    }
    const epoch = this.localWriteEpoch
    await this.writeSurvivalDocument(payload, authority)
    if (epoch !== this.localWriteEpoch) return
    await this.consolidateBlocks(payload, authority)
    this.onConsolidated?.(authority)
    this.onConsolidationActivity?.({ active: false })
  }

  private buildActivePayload(blocks: SpaceEntry[], metaBlock = this.metaBlock): SpaceEntry[] {
    return metaBlock ? [...blocks, cloneBlock(metaBlock)] : blocks
  }

  private async writePendingSurvivalDocument(): Promise<void> {
    const pending = this.pendingWorkbench
    this.pendingWorkbench = undefined
    this.saveTimer = undefined
    if (!pending) return
    const blocksWithMeta = this.buildActivePayload(pending.blocks, pending.metaBlock)
    if (!blocksWithMeta.length) {
      const cleared = await this.database.clearLocalMemoryForVolume(pending.authority)
      if (!cleared) {
        throw new Error('Pending local clear blocked: mounted volume changed')
      }
      return
    }
    await this.writeSurvivalDocument(blocksWithMeta, pending.authority)
  }

  private async finishPendingConsolidation(): Promise<void> {
    const epoch = this.localWriteEpoch
    this.consolidationTimer = undefined
    const pendingShelf = this.pendingShelf
    const authority = pendingShelf?.authority ?? this.pendingConsolidationAuthority
    this.pendingShelf = undefined
    this.pendingConsolidationAuthority = undefined
    try {
      await this.writePendingSurvivalDocument()
      if (epoch !== this.localWriteEpoch) return
      if (authority === undefined) return
      if (pendingShelf) {
        const blocksWithMeta = this.buildActivePayload(pendingShelf.blocks, pendingShelf.metaBlock)
        if (!blocksWithMeta.length) {
          const cleared = await this.database.clearLocalMemoryForVolume(authority)
          if (!cleared) {
            throw new Error('Pending shelf clear blocked: mounted volume changed')
          }
        } else {
          await this.consolidateBlocks(blocksWithMeta, authority)
        }
      } else {
        await this.consolidateActiveDocument(authority)
      }
      this.onConsolidated?.(authority)
    } finally {
      this.onConsolidationActivity?.({ active: false })
    }
  }

  private enqueueLocalWrite<T>(write: () => Promise<T>): Promise<T> {
    const next = this.localWriteTail.then(write)
    this.localWriteTail = next.catch(() => undefined)
    return next
  }

  private async writeSurvivalDocument(
    blocksWithMeta: SpaceEntry[],
    authority: LocalVolumeAuthority
  ): Promise<void> {
    const epoch = this.localWriteEpoch
    return this.enqueueLocalWrite(async () => {
      if (epoch !== this.localWriteEpoch) return
      const { metaBlock } = extractLilodataBlock(blocksWithMeta)
      if (!metaBlock) {
        throw new Error('Workbench write blocked: missing /lilodata')
      }
      const replaced = await this.database.replaceEditorBlocksWithWorkbenchMarker(
        blocksWithMeta,
        this.buildWorkbenchMarker(authority, metaBlock.ided[0])
      )
      if (!replaced.ok) {
        this.notifyWriteRefusal(replaced.reason)
        throw new Error(`Workbench write blocked: ${replaced.reason}`)
      }
    })
  }

  private async consolidateActiveDocument(authority: LocalVolumeAuthority): Promise<void> {
    const epoch = this.localWriteEpoch
    const blocksWithMeta = await this.database.getEditorMemory().loadEditorBlocks()
    if (epoch !== this.localWriteEpoch || !blocksWithMeta.length) return
    await this.consolidateBlocks(blocksWithMeta, authority)
  }

  private async consolidateBlocks(
    blocksWithMeta: SpaceEntry[],
    authority: LocalVolumeAuthority
  ): Promise<void> {
    const epoch = this.localWriteEpoch
    return this.enqueueLocalWrite(async () => {
      if (epoch !== this.localWriteEpoch) return
      if (!blocksWithMeta.length) return
      const result = await this.database.updateVolumeSpace({
        authority,
        blocksWithMeta,
        workbench: false,
        payloadState: 'hydrated'
      })
      if (!result.ok) {
        this.notifyWriteRefusal(result.reason)
        throw new Error(`Shelf write blocked: ${result.reason}`)
      }
    })
  }

  async getActiveSpaceMeta(
    authority = this.captureVolumeAuthority()
  ): Promise<LocalSpaceMeta | undefined> {
    if (!this.metaBlock) return undefined
    return (
      await this.database.loadVolumeShelfSpace(
        authority,
        this.metaBlock.ided[0]
      )
    )?.meta
  }

  getDatabaseMemory(): DatabaseMemory {
    return this.database
  }

  getMountedPassport(): LocalAccountPassport | undefined {
    return this.mountedPassport
      ? this.clonePassport(this.mountedPassport)
      : undefined
  }

  getVolumeAuthority(): LocalVolumeAuthority {
    return this.cloneVolumeAuthority(this.captureVolumeAuthority())
  }

  matchesVolumeAuthority(authority: LocalVolumeAuthority): boolean {
    const current = this.captureVolumeAuthority()
    if (!current || !authority) return current === authority
    return (
      current.owner_id === authority.owner_id
      && current.mount_id === authority.mount_id
    )
  }

  async isVolumeAuthorityCurrent(authority: LocalVolumeAuthority): Promise<boolean> {
    if (!this.matchesVolumeAuthority(authority)) return false
    return this.database.isVolumeAuthorityCurrent(authority)
  }

  async listVolumeSpacesMeta(
    authority = this.captureVolumeAuthority()
  ): Promise<LocalSpaceMeta[]> {
    const rows = await this.database.listVolumeSpacesMeta(authority)
    if (!rows) {
      throw new Error('Volume read blocked: mounted volume changed')
    }
    return rows
  }

  async listVolumeMemoryRows(
    authority = this.captureVolumeAuthority()
  ): Promise<LocalMemoryIndexRow[]> {
    const rows = await this.database.listVolumeMemoryRows(authority)
    if (!rows) {
      throw new Error('Memory read blocked: mounted volume changed')
    }
    return rows
  }

  async loadVolumeShelfSpace(
    spaceId: string,
    authority = this.captureVolumeAuthority()
  ): Promise<LocalShelfSpace | undefined> {
    return this.database.loadVolumeShelfSpace(authority, spaceId)
  }

  async preflightVolumeShelfExport(
    authority = this.captureVolumeAuthority()
  ): Promise<VolumeShelfExportPreflight> {
    const result = await this.database.preflightVolumeShelfExport(authority)
    if (!result.ok) return result
    for (const space of result.spaces) {
      try {
        const blocksWithMeta = await decodeSpacePayload(space.blob.payload)
        if (!this.hasSpaceIdentity(blocksWithMeta, space.meta.space_id)) {
          throw new Error('Space payload identity mismatch')
        }
        space.blob.payload = await encodeSpacePayload(blocksWithMeta)
      } catch {
        return {
          ok: false,
          reason: 'invalid_payload',
          space_id: space.meta.space_id
        }
      }
    }
    return result
  }

  async upsertRemoteSpacesMetaForVolume(
    authority: LocalVolumeAuthority,
    input: RemoteSpaceMetaInput[],
    now?: string
  ): Promise<LocalSpaceMeta[]> {
    const rows = await this.database.upsertRemoteSpacesMetaForVolume(
      authority,
      input,
      now
    )
    if (!rows) {
      throw new Error('Remote metadata write blocked: mounted volume changed')
    }
    return rows
  }

  async replaceVolumeBlobFromRemote(
    authority: LocalVolumeAuthority,
    input: ReplaceLocalBlobFromRemoteInput
  ): Promise<ReplaceLocalBlobFromRemoteResult> {
    return this.database.replaceVolumeBlobFromRemote(authority, input)
  }

  async writeMergedVolumeSpace(
    authority: LocalVolumeAuthority,
    input: {
      expectedPayload?: string
      blocksWithMeta: SpaceEntry[]
      spaceSlug?: string | null
      dirty?: boolean
      payloadState?: PayloadState
      now?: string
    }
  ): Promise<LocalSpaceMeta | undefined> {
    return this.enqueueLocalWrite(async () => {
      const { metaBlock } = extractLilodataBlock(input.blocksWithMeta)
      const spaceId = metaBlock?.ided[0] ?? deriveSpaceIded(input.blocksWithMeta).createdId
      const existing = await this.database.loadVolumeShelfSpace(authority, spaceId)
      const write = existing
        ? this.database.updateVolumeSpace.bind(this.database)
        : this.database.fileVolumeSpace.bind(this.database)
      const result = await write({
        authority,
        ...input,
        workbench: false,
        dirty: input.dirty ?? true,
        payloadState: input.payloadState ?? 'hydrated'
      })
      if (!result.ok && result.reason === 'local_changed') return undefined
      if (!result.ok) {
        throw new Error(`Merged volume write blocked: ${result.reason}`)
      }
      return result.meta
    })
  }

  async markVolumeSpaceDeleted(
    authority: LocalVolumeAuthority,
    spaceId: string,
    deletedAt: string,
    now?: string
  ): Promise<LocalSpaceMeta | undefined> {
    return this.database.markVolumeSpaceDeleted(
      authority,
      spaceId,
      deletedAt,
      now
    )
  }

  async purgeDeletedVolumePayload(
    authority: LocalVolumeAuthority,
    spaceId: string,
    now?: string
  ): Promise<LocalSpaceMeta | undefined> {
    return this.database.purgeDeletedVolumePayload(authority, spaceId, now)
  }

  async setVolumePayloadState(
    authority: LocalVolumeAuthority,
    spaceId: string,
    payloadState: PayloadState,
    opts?: { errorAt?: string | null, spaceEd?: string }
  ): Promise<void> {
    const updated = await this.database.updateSpaceFlagsForVolume(
      authority,
      spaceId,
      {
        payloadState,
        payloadErrorAt: opts?.errorAt,
        spaceEd: opts?.spaceEd
      }
    )
    if (!updated) {
      throw new Error('Payload-state write blocked: mounted volume changed')
    }
  }

  async recordVolumeSyncSuccess(
    authority: LocalVolumeAuthority,
    spaceId: string,
    syncAt: string | null,
    now?: string,
    canonical?: SyncAcknowledgement
  ): Promise<void> {
    const updated = await this.database.recordVolumeSyncSuccess(
      authority,
      spaceId,
      syncAt,
      now,
      canonical
    )
    if (!updated) {
      throw new Error('Sync proof write blocked: mounted volume changed')
    }
  }

  async renameVolumeSpace(
    authority: LocalVolumeAuthority,
    spaceId: string,
    spaceSlug: string,
    spaceEditedId: string,
    now?: string,
    expectedPayload?: string
  ): Promise<LocalSpaceMeta | undefined> {
    const renamed = await this.database.renameVolumeSpace(
      authority,
      spaceId,
      spaceSlug,
      spaceEditedId,
      now,
      expectedPayload
    )
    if (renamed && this.metaBlock?.ided[0] === spaceId) {
      this.metaBlock = {
        ...this.metaBlock,
        text: updateLilodataText(this.metaBlock.text, { slug: spaceSlug }),
        ided: [spaceId, spaceEditedId]
      }
    }
    return renamed
  }

  async deleteVolumeSpace(
    authority: LocalVolumeAuthority,
    spaceId: string,
    seedProof?: SeedSpaceProof
  ): Promise<boolean> {
    return this.database.deleteVolumeSpace(authority, spaceId, seedProof)
  }

  async listUnsafeVolumeRows(
    authority: AccountUnmountAuthority
  ): Promise<UnsafeOwnerShelfRow[]> {
    const rows = await this.database.listUnsafeVolumeRows(authority)
    if (!rows) {
      throw new Error('Debt read blocked: mounted volume changed')
    }
    return rows
  }

  async flushActiveDocument(blocks: SpaceEntry[]): Promise<void> {
    await this.flushActiveDocumentForVolume(blocks, this.captureVolumeAuthority())
  }

  async flushActiveDocumentForVolume(
    blocks: SpaceEntry[],
    authority: LocalVolumeAuthority,
    expectedPayload?: string
  ): Promise<void> {
    this.localWriteEpoch += 1
    if (this.saveTimer) {
      clearTimeout(this.saveTimer)
      this.saveTimer = undefined
    }
    if (this.consolidationTimer) {
      clearTimeout(this.consolidationTimer)
      this.consolidationTimer = undefined
    }
    this.pendingWorkbench = undefined
    this.pendingShelf = undefined
    this.pendingConsolidationAuthority = undefined
    if (expectedPayload !== undefined) {
      const blocksWithMeta = this.buildActivePayload(blocks)
      await this.enqueueLocalWrite(async () => {
        const result = await this.database.updateVolumeSpace({
          authority, blocksWithMeta, expectedPayload, workbench: true, payloadState: 'hydrated'
        })
        if (!result.ok) throw new Error(`Sync workbench write blocked: ${result.reason}`)
      })
      this.onConsolidationActivity?.({ active: false })
      return
    }
    await this.doSaveDocumentForAuthority(blocks, authority)
  }

  async flushActiveDocumentForOwner(
    blocks: SpaceEntry[],
    ownerId: string | null,
    opts: {
      spaceSlug?: string | null
      dirty?: boolean
      authority?: LocalVolumeAuthority
    } = {}
  ): Promise<void> {
    this.cancelPendingSave()
    const authority = opts.authority !== undefined
      ? this.cloneVolumeAuthority(opts.authority)
      : this.resolveWriteAuthority(ownerId)
    if ((authority?.owner_id ?? null) !== ownerId) {
      throw new Error('Owner flush blocked: owner is outside the captured volume')
    }
    if (this.activeDeskState === 'provisional') return
    if (this.activeDeskState === 'filing') {
      this.queueFilingSnapshot(blocks, authority)
      await this.waitForActiveFiling()
      return
    }
    const blocksWithMeta = this.buildActivePayload(blocks)
    if (!blocksWithMeta.length) {
      const cleared = await this.database.clearLocalMemoryForVolume(authority)
      if (!cleared) {
        throw new Error('Owner flush clear blocked: mounted volume changed')
      }
      return
    }
    await this.writeSurvivalDocument(blocksWithMeta, authority)
    const result = await this.database.updateVolumeSpace({
      authority,
      blocksWithMeta,
      workbench: false,
      spaceSlug: opts.spaceSlug,
      dirty: opts.dirty,
      payloadState: 'hydrated'
    })
    if (!result.ok) {
      this.notifyWriteRefusal(result.reason)
      throw new Error(`Owner shelf write blocked: ${result.reason}`)
    }
    this.onConsolidated?.(authority)
    this.onConsolidationActivity?.({ active: false })
  }

  async markActiveSpaceDirty(dirty: boolean, now?: string): Promise<void> {
    if (!this.metaBlock) return
    await this.markVolumeSpaceDirty(
      this.captureVolumeAuthority(),
      this.metaBlock.ided[0],
      dirty,
      now
    )
  }

  async markVolumeSpaceDirty(
    authority: LocalVolumeAuthority,
    spaceId: string,
    dirty: boolean,
    now?: string
  ): Promise<void> {
    const updated = await this.database.updateSpaceFlagsForVolume(
      authority,
      spaceId,
      { dirty },
      now
    )
    if (!updated) {
      throw new Error('Dirty-state write blocked: mounted volume changed')
    }
  }

  async setActivePayloadState(payloadState: PayloadState, opts?: { errorAt?: string | null }): Promise<void> {
    if (!this.metaBlock) return
    const updated = await this.database.updateSpaceFlagsForVolume(
      this.captureVolumeAuthority(),
      this.metaBlock.ided[0],
      {
        payloadState,
        payloadErrorAt: opts?.errorAt
      }
    )
    if (!updated) {
      throw new Error('Payload-state write blocked: mounted volume changed')
    }
  }

  exportToJSON(blocks: SpaceEntry[]): string {
    const payload = blocks.map(block => cloneSpaceEntry(block))
    if (this.metaBlock && !payload.some(block => isLilodataBlock(block))) {
      payload.push(cloneBlock(this.metaBlock))
    }
    const pretty = JSON.stringify(payload, null, 2)
    const withArrays = this.inlineArrays(pretty, ['xyzw'])
    const withMeta = this.inlineMeta(withArrays)
    return this.hugClosingBrackets(withMeta, ['ided', 'link'])
  }

  async importFromJSON(
    json: string,
    opts?: {
      ownerId?: string | null
      authority?: LocalVolumeAuthority
    }
  ): Promise<SpaceEntry[]> {
    await this.waitForActiveFiling()
    const authority = opts?.authority !== undefined
      ? this.cloneVolumeAuthority(opts.authority)
      : this.resolveWriteAuthority(opts?.ownerId)
    const authorityOwnerId = authority?.owner_id ?? null
    if (opts?.ownerId !== undefined && opts.ownerId !== authorityOwnerId) {
      throw new Error('Import blocked: owner is outside the captured volume')
    }
    const previousMetaBlock = this.metaBlock
    const previousMetaNeedsSave = this.metaNeedsSave
    try {
      const blocks = await decodeSpacePayload(json)

      const { blocks: filtered, metaBlock } = extractLilodataBlock(blocks)
      const normalizedMeta = this.normalizeMetaBlock(filtered, metaBlock)
      this.metaBlock = normalizedMeta ? cloneBlock(normalizedMeta) : undefined
      if (!filtered.length && !normalizedMeta) {
        const cleared = await this.database.clearLocalMemoryForVolume(authority)
        if (!cleared) {
          throw new Error('Import clear blocked: mounted volume changed')
        }
        this.metaNeedsSave = false
        this.activeDeskState = 'filed'
        this.provisionalComparable = undefined
        this.bootLanding = { kind: 'proven-empty' }
        return []
      }
      const writeInput = {
        authority,
        blocksWithMeta: normalizedMeta
          ? [...filtered, cloneBlock(normalizedMeta)]
          : filtered,
        workbench: true,
        dirty: Boolean(authority),
        payloadState: 'hydrated'
      } as const
      let result = await this.database.fileVolumeSpace(writeInput)
      if (!result.ok && result.reason === 'space_exists') {
        result = await this.database.updateVolumeSpace(writeInput)
      }
      if (!result.ok) {
        throw new Error(`Space filing blocked: ${result.reason}`)
      }
      this.metaNeedsSave = false
      this.activeDeskState = 'filed'
      this.provisionalComparable = undefined
      this.bootLanding = { kind: 'opened' }
      return filtered
    } catch (error) {
      this.metaBlock = previousMetaBlock
      this.metaNeedsSave = previousMetaNeedsSave
      throw new Error(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  private cancelPendingSave(): void {
    this.localWriteEpoch += 1
    if (this.saveTimer) {
      clearTimeout(this.saveTimer)
      this.saveTimer = undefined
    }
    if (this.consolidationTimer) {
      clearTimeout(this.consolidationTimer)
      this.consolidationTimer = undefined
      this.onConsolidationActivity?.({ active: false })
    }
    this.pendingWorkbench = undefined
    this.pendingShelf = undefined
    this.pendingConsolidationAuthority = undefined
  }

  private notifyWriteRefusal(reason: string): void {
    if (reason === 'space_missing' || reason === 'space_deleted') {
      this.onWriteRefused?.(reason)
    }
  }

  getMetaBlock(): Block | undefined {
    return this.metaBlock ? cloneBlock(this.metaBlock) : undefined
  }

  setMetaBlock(block?: Block): void {
    this.metaBlock = block ? cloneBlock(block) : undefined
  }

  consumeMetaNeedsSave(): boolean {
    const needsSave = this.metaNeedsSave
    this.metaNeedsSave = false
    return needsSave
  }

  private captureVolumeAuthority(): LocalVolumeAuthority {
    if (this.volumeAuthority === undefined) {
      throw new Error('Local volume is not mounted')
    }
    return this.cloneVolumeAuthority(this.volumeAuthority)
  }

  private resolveWriteAuthority(ownerId?: string | null): LocalVolumeAuthority {
    const authority = this.captureVolumeAuthority()
    const mountedOwnerId = authority?.owner_id ?? null
    if (ownerId !== undefined && ownerId !== mountedOwnerId) {
      throw new Error('Local write blocked: owner is outside the mounted volume')
    }
    return authority
  }

  private assertSnapshotAuthority(snapshot: LocalVolumeBootSnapshot): void {
    const expected = this.captureVolumeAuthority()
    const actual = snapshot.passport
      ? {
          owner_id: snapshot.passport.owner_id,
          mount_id: snapshot.passport.mount_id
        }
      : null
    if (
      (expected === null) !== (actual === null)
      || (
        expected !== null
        && actual !== null
        && (
          expected.owner_id !== actual.owner_id
          || expected.mount_id !== actual.mount_id
        )
      )
    ) {
      throw new LocalBootRestartRequired()
    }
  }

  private matchesWorkbenchAuthority(
    marker: LocalWorkbenchMarker,
    authority: LocalVolumeAuthority
  ): boolean {
    return authority
      ? marker.owner_id === authority.owner_id && marker.mount_id === authority.mount_id
      : marker.owner_id === null && marker.mount_id === null
  }

  private buildWorkbenchMarker(
    authority: LocalVolumeAuthority,
    spaceId: string
  ): LocalWorkbenchMarker {
    return authority
      ? {
          owner_id: authority.owner_id,
          mount_id: authority.mount_id,
          space_id: spaceId
        }
      : {
          owner_id: null,
          mount_id: null,
          space_id: spaceId
        }
  }

  private hasSpaceIdentity(blocksWithMeta: SpaceEntry[], spaceId: string): boolean {
    const { metaBlock } = extractLilodataBlock(blocksWithMeta)
    return metaBlock?.ided[0] === spaceId
  }

  private cloneVolumeAuthority(authority: LocalVolumeAuthority): LocalVolumeAuthority {
    return authority ? { ...authority } : null
  }

  private clonePassport(passport: LocalAccountPassport): LocalAccountPassport {
    return passport.kind === 'verified'
      ? {
          ...passport,
          profile: { ...passport.profile }
        }
      : { ...passport }
  }

  private isDeactivatedAccountVolume(): boolean {
    return Boolean(
      this.mountedPassport?.kind === 'verified'
      && this.mountedPassport.profile.account_state === 'deactivated'
    )
  }

  setupAutoSave(): void {
    window.addEventListener('beforeunload', () => {
      if (this.saveTimer) {
        clearTimeout(this.saveTimer)
        const pending = this.pendingWorkbench
        this.pendingWorkbench = undefined
        if (pending) {
          void this.doSaveDocumentForAuthority(pending.blocks, pending.authority, pending.metaBlock)
        }
      }
    })
  }

  // Inline selected arrays (e.g., xyzw, ided) while keeping other fields pretty-printed.
  private inlineArrays(json: string, keys: string[]): string {
    if (!keys.length) return json
    const pattern = new RegExp(`"(${keys.join('|')})"\\s*:\\s*\\[(.*?)\\]`, 'gs')
    return json.replace(pattern, (match, key, contents) => {
      try {
        const parsed = JSON.parse(`[${contents}]`)
        if (!Array.isArray(parsed)) return match
        const formatted = parsed.map((value: unknown) => {
          if (typeof value === 'string') return JSON.stringify(value)
          if (typeof value === 'number') return String(value)
          if (value === null) return 'null'
          if (typeof value === 'boolean') return value ? 'true' : 'false'
          return JSON.stringify(value)
        })
        return `"${key}": [${formatted.join(', ')}]`
      } catch {
        return match
      }
    })
  }

  private inlineMeta(json: string): string {
    const pattern = /"meta"\s*:\s*\{([^{}]*)\}/g
    return json.replace(pattern, (match, body) => {
      if (!body.trim()) return `"meta": {}`
      try {
        const meta = JSON.parse(`{${body}}`)
        const keys = Object.keys(meta)
        const inline = keys.map(key => `"${key}": ${meta[key]}`).join(', ')
        return `"meta": { ${inline} }`
      } catch {
        return match
      }
    })
  }

  // Keep arrays multi-line but move the closing bracket up to the last entry line.
  private hugClosingBrackets(json: string, keys: string[]): string {
    if (!keys.length) return json
    const pattern = new RegExp(`("(?:${keys.join('|')})"\\s*:\\s*\\[\\n)([\\s\\S]*?)(\\n\\s*\\])(,?)([ \\t]*\\n?)`, 'g')
    return json.replace(pattern, (_match, start, body, _closing, comma, trailingNewline) => {
      const trimmedBody = body.replace(/\s+$/g, '')
      const newline = trailingNewline && trailingNewline.length ? '\n' : ''
      return `${start}${trimmedBody}]${comma}${newline}`
    })
  }

  private normalizeMetaBlock(blocks: SpaceEntry[], metaBlock?: Block): Block | undefined {
    if (!metaBlock) return undefined
    if (!metaBlock.ided || metaBlock.ided.length !== 2) return metaBlock
    const [createdId, editedId] = metaBlock.ided
    const isLegacy = createdId === LILODATA_BLOCK_ID || editedId === LILODATA_BLOCK_ID
    if (!isLegacy) return metaBlock
    const derived = deriveSpaceIded(blocks)
    this.metaNeedsSave = true
    return { ...metaBlock, ided: [derived.createdId, derived.editedId] }
  }

  async loadSeedBlocks(): Promise<Block[] | null> {
    if (this.seedBlocksCache) {
      return this.seedBlocksCache.map(block => cloneBlock(block))
    }
    try {
      const controller = new AbortController()
      const timeoutId: ReturnType<typeof setTimeout> = setTimeout(() => {
        controller.abort()
      }, FETCH_TIMEOUT_MS)
      const response = await fetch('/seed/seed.json', {
        cache: 'no-cache',
        signal: controller.signal,
      }).finally(() => {
        clearTimeout(timeoutId)
      })
      if (!response.ok) {
        throw new Error(`Seed fetch failed with status ${response.status}`)
      }
      const data = await response.json()
      if (!Array.isArray(data)) {
        throw new Error('Seed JSON is not an array')
      }

      const blocks: Block[] = []
      for (const item of data) {
        if (!isValidBlock(item)) {
          throw new Error('Seed JSON contains an invalid block')
        }
        blocks.push(cloneBlock(item))
      }
      this.seedBlocksCache = blocks.map(block => cloneBlock(block))
      return blocks
    } catch (error) {
      console.error('Failed to load seed JSON:', error)
      return null
    }
  }
}

function blocksMatchStructurally(left: SpaceEntry[], right: SpaceEntry[]): boolean {
  return JSON.stringify(sortBlocks(left).map(block => cloneSpaceEntry(block)))
    === JSON.stringify(sortBlocks(right).map(block => cloneSpaceEntry(block)))
}
